import { useCallback } from "react";
import { useAuth } from "../global-components/src";

const STORAGE_KEY = "pwd_cache";
const TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  v: string; // obfuscated value
  exp: number; // expiry timestamp
}

/** XOR-obfuscate the value using the key (base64-safe). */
function obfuscate(text: string, key: string): string {
  if (!key) return btoa(text);
  return btoa(
    text
      .split("")
      .map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length)))
      .join("")
  );
}

function deobfuscate(encoded: string, key: string): string {
  try {
    const raw = atob(encoded);
    if (!key) return raw;
    return raw
      .split("")
      .map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length)))
      .join("");
  } catch {
    return "";
  }
}

/**
 * Temporary in-session password cache.
 * - Stores an obfuscated copy in sessionStorage (tab-scoped, cleared on close).
 * - Automatically expires after TTL_MS (5 min).
 * - Keyed to the current auth token so it's invalidated on logout/re-login.
 */
export function usePasswordCache() {
  const { token } = useAuth();
  // Use the last 16 chars of the token as the obfuscation key so each session
  // gets a unique key without storing the full token as a map key.
  const key = token ? token.slice(-16) : "";

  const savePassword = useCallback(
    (password: string) => {
      if (!password) return;
      const entry: CacheEntry = {
        v: obfuscate(password, key),
        exp: Date.now() + TTL_MS,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    },
    [key]
  );

  const getPassword = useCallback((): string => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return "";
      const entry: CacheEntry = JSON.parse(raw);
      if (Date.now() > entry.exp) {
        sessionStorage.removeItem(STORAGE_KEY);
        return "";
      }
      return deobfuscate(entry.v, key);
    } catch {
      return "";
    }
  }, [key]);

  const clearPassword = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  return { savePassword, getPassword, clearPassword };
}
